import React from 'react';
import styled from 'styled-components';
import EVENTS from 'Jsx/Shared/Tracking/events';
import { useTranslation } from 'react-i18next';

import Text from 'crehana-ui/Text';
import { baseMain, white } from 'crehana-ui/styles/colors';
import PrimaryButton from 'crehana-ui/Buttons/PrimaryButton';
import { string } from 'prop-types';

import { MAIN_FOOTER_KEY } from 'Jsx/Utils/transformKeys';
import localesEs from 'Jsx/MainFooter/locales/es';
import Lazyload from 'Jsx/Shared/Lazyload';

/* Icons */
import Twitter from 'crehana-icons/Twitter';
import Facebook from 'crehana-icons/Facebook';
import Instagram from 'crehana-icons/Instagram';
import Linkedin from 'crehana-icons/Linkedin';
import Youtube from 'crehana-icons/Youtube';
import GooglePlayIcon from 'crehana-icons/GooglePlayIcon';
import Apple from 'crehana-icons/Apple';
import Envelope from 'crehana-icons/Envelope';

import CurrencyDropDownSelector from '../CurrencyDropdownSelector/CurrencyDropdownSelector';
import PaymentLogos from '../PaymentLogos';
import Collapsible from './Collapsible';

import {
  SocialNetworks,
  IconLink,
  Apps,
  Copyright,
  CurrencySelectorWrapper,
  Details,
} from './MobileFooter.styles';

import {
  // CREATIVE_CLOUD,
  COMMUNITY,
  US,
  itemsWithPrefix,
} from '../../constants';

const SOCIAL_NETWORKS = [
  {
    link: 'https://www.facebook.com/Crehanacom/',
    Icon: Facebook,
  },
  {
    link: 'https://twitter.com/crehanacom',
    Icon: Twitter,
  },
  {
    link: 'https://www.instagram.com/crehanacom/',
    Icon: Instagram,
  },
  {
    link: 'https://www.linkedin.com/school/crehana/',
    Icon: Linkedin,
  },
  {
    link: 'https://www.youtube.com/channel/UCg6_yPdtalblZ0Peqjv5rPg/featured',
    Icon: Youtube,
  },
];

const LogosWrapper = styled.div`
  max-width: 258px;
  max-height: 68px;
  overflow: hidden;
  @media (min-width: 768px) {
    max-width: none;
    max-height: none;
  }

  & .paymentLogo {
    margin-left: 4px;
    margin-right: 4px;
    @media (min-width: 768px) {
      margin-left: 8px;
      margin-right: 8px;
    }
  }
`;

const MobileFooter = ({
  countryPrefix,
  customTextColor,
  // customBgColor,
  siteUrl,
  categories,
  softwares,
  isDark,
}) => {
  const { t } = useTranslation(MAIN_FOOTER_KEY);

  const HELP = textColor => [
    {
      link: '',
      label: countryPrefix === 'br' ? 'oi@crehana.com' : 'hola@crehana.com',
      icon: <Envelope size={16} color={textColor} />,
    },
    {
      link: 'https://crehana.zendesk.com/hc/es',
      label: 'Ayuda',
    },
  ];

  return (
    <div>
      <div>
        <Collapsible
          title="Categorías"
          items={categories}
          prefix="Cursos de"
          color={isDark ? white : baseMain}
        />
        <Collapsible
          title="Software"
          items={softwares}
          prefix="Cursos de"
          color={isDark ? white : baseMain}
        />
        <Collapsible
          title={t('FOOTER_MEET_US', {
            defaultValue: localesEs.es.translation.FOOTER_MEET_US,
          })}
          items={itemsWithPrefix(US(t, countryPrefix), countryPrefix, siteUrl)}
          color={isDark ? white : baseMain}
        />
        <Collapsible
          title={t('FOOTER_HELP', {
            defaultValue: localesEs.es.translation.FOOTER_HELP,
          })}
          items={HELP(customTextColor)}
          color={isDark ? white : customTextColor}
        />
        {countryPrefix !== 'br' && (
          <Collapsible
            title={t('COMMUNITY_TITLE', {
              defaultValue: localesEs.es.translation.COMMUNITY_TITLE,
            })}
            items={itemsWithPrefix(COMMUNITY(t), countryPrefix, siteUrl)}
            prefix=""
            color={isDark ? white : customTextColor}
          />
        )}
        <Details>
          <SocialNetworks>
            {SOCIAL_NETWORKS.map((el, index) => (
              <IconLink
                aria-label={el.link}
                key={index}
                href={el.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                <el.Icon size={24} color={isDark ? white : customTextColor} />
              </IconLink>
            ))}
          </SocialNetworks>
          <Apps>
            <Text
              type="label"
              className="text-14 justify-center flex mb-20"
              color="gray-dark"
            >
              {t('FOOTER_DOWNLOAD_MOBILE', {
                defaultValue: localesEs.es.translation.FOOTER_DOWNLOAD_MOBILE,
              })}
            </Text>
            <div
              className="flex flex-col justify-center"
              style={{ width: 258 }}
            >
              <PrimaryButton
                onClick={() => {
                  window.track(EVENTS.DOWNLOAD_APPS.PLAY_STORE);
                  window.open(
                    'https://play.google.com/store/apps/details?id=com.crehana.android',
                    '_blank',
                  );
                }}
                label="Google play"
                leftIcon={{
                  component: GooglePlayIcon,
                  size: 18,
                  color: 'white',
                }}
                className="mb-20"
                full
                darkMode={isDark}
              />
              <PrimaryButton
                onClick={() => {
                  window.track(EVENTS.DOWNLOAD_APPS.APP_STORE);
                  window.open(
                    'https://itunes.apple.com/us/app/crehana/id1353031284',
                    '_blank',
                  );
                }}
                label="App store"
                leftIcon={{
                  component: Apple,
                  size: 19,
                  color: 'white',
                }}
                labelClassName="mt-4"
                full
                darkMode={isDark}
              />
            </div>
          </Apps>
          <CurrencySelectorWrapper>
            <Lazyload placeholder={<div className="h-56" />}>
              <CurrencyDropDownSelector isDark={isDark} />
            </Lazyload>
          </CurrencySelectorWrapper>
          <Lazyload placeholder={<div style={{ height: 18 }} />}>
            <LogosWrapper className="flex items-center flex-wrap justify-center mt-24 ml-auto mr-auto">
              <PaymentLogos classNameItem="mt-8 mb-8 paymentLogo" />
            </LogosWrapper>
          </Lazyload>
          <Copyright id="mobile-main-footer-copy-and-legals">
            <Text
              type="body4"
              color="gray-dark"
              className="block mt-16 mr-auto ml-auto text-center"
              style={{ fontSize: 12, maxWidth: 180 }}
            >
              Copyright © Crehana {new Date().getFullYear()} -{' '}
              {t('FOOTER_RIGHTS', {
                defaultValue: localesEs.es.translation.FOOTER_RIGHTS,
              })}
            </Text>
          </Copyright>
        </Details>
      </div>
    </div>
  );
};

MobileFooter.defaultProps = {
  countryPrefix: '',
  customTextColor: baseMain,
  customBgColor: '#fff',
  siteUrl: '',
};

MobileFooter.propTypes = {
  customTextColor: string,
  customBgColor: string,
  countryPrefix: string,
  siteUrl: string,
};

export default MobileFooter;
